/**
 * <ia-narvar-modern>: brings Narvar's Modern shipping-protection widget to the design.
 *
 * Why this exists at all. Modern renders inside nested shadow roots and exports no
 * parts from its widget variants, so the three things the design needs (drop the
 * blurb, drop their logo, put the info icon hard right) cannot be reached from a
 * stylesheet in the document. Its colours, type, padding and surface CAN: those are
 * set as tokens in ia-cart-footer.css and are NOT repeated here. This file is only for
 * what tokens cannot express.
 *
 * How. Their shadow roots are `mode: "open"`, so a constructed stylesheet can be
 * adopted into each one. `adoptedStyleSheets` lives on the ShadowRoot rather than in
 * the render template, so Lit re-rendering its content does not drop it, and no
 * element is ever moved or removed: their DOM stays theirs, including the info icon
 * that owns the explainer popup.
 *
 * What this is standing on. Their internal, undocumented class names. That is the same
 * exposure the Legacy CSS already has with `.narvar-cart-widget-*`, but here it is
 * spread over four shadow roots, so a vendor update can quietly leave the strip in
 * their layout instead of ours. `verify()` therefore checks the result and complains
 * in the console instead of failing silently. Turn it on with `?ia-debug` in the URL
 * or `localStorage.setItem('ia-debug', '1')`, same switch as ia-cart-upsell.js.
 */

import { debugEnabled } from "ia-util-helpers";

const DEBUG = debugEnabled();

function debug(label, data) {
  if (DEBUG) console.log(`[ia-narvar-modern] ${label}`, data ?? "");
}

/* One rule set per shadow root, keyed by the root we adopt it into.
 *
 * The layout is one flex row, which means every wrapper between the card and the three
 * leaves has to stop being a box. `display: contents` does that, and it works across a
 * shadow boundary when applied to a host: the host's box goes away and its shadow
 * content joins the parent's flex layout. That is what lets an element sitting two
 * roots down take `order` and `margin-left` from the card's row.
 *
 * Sizes are not repeated from the design here. Gap and padding come from the tokens in
 * ia-cart-footer.css, so there is one source for them. */
const SHEETS = {
  /* The variant's own tree: the blurb goes, the header wrappers collapse. */
  variant: `
    widget-description { display: none; }
    widget-header,
    widget-title,
    div.title-wrapper,
    div.title,
    div.action-wrapper { display: contents; }
  `,
  /* The card is the row itself. Its margins come from the card-spacing token;
     alignment and gap are the parts tokens cannot set. The 4px gap is what turns
     title and price into one sentence (`Your order is protected for $4.93`), the
     same value the Legacy CSS uses between its label and price. Letter-spacing is
     the design's -1%, inherited by every leaf across the shadow boundaries. */
  card: `
    .card {
      display: flex;
      align-items: center;
      gap: 4px;
      letter-spacing: -0.01em;
    }
  `,
  /* The header's wrapper is one more box in the way. */
  header: `
    .header { display: contents; }
  `,
  /* Inside the title: their logo is not in the design, and the info icon goes to the
     far right. `margin-left: auto` claims the free space, `order` puts it after the
     price, which is a sibling only because of the collapsing above. */
  title: `
    .title-content { display: contents; }
    img.icon { display: none; }
    narvar-info-icon {
      order: 1;
      flex: none;
      margin-left: auto;
    }
  `,
};

class IaNarvarModern extends HTMLElement {
  connectedCallback() {
    this.sheets = new Map();
    this.observers = new Map();
    this.widget = this.querySelector("narvar-shipping-protection-widget");
    if (!this.widget) return;

    /* The element is in the markup whichever widget version the merchant picked, and
       under Legacy this tag is never registered, so there is nothing to style and
       nothing to wait for. */
    customElements.whenDefined("narvar-shipping-protection-widget").then(() => {
      debug("Modern widget registered, waiting for its shadow content");
      this.schedule();
    });
  }

  disconnectedCallback() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();
  }

  /* The tree arrives in stages: the widget renders its variant, the variant renders the
     card, the card's children render inside their own roots. A MutationObserver cannot
     follow that on its own, because `subtree` does not cross a shadow boundary, so an
     observer on the widget's root never sees the card appear. Measured the hard way:
     the blurb was hidden while the card and title rules had silently found no root.
     So retry by frame until every root has been served, and keep the observers for
     what comes later, which is the variant being replaced when protection is toggled. */
  schedule(attempt = 0) {
    if (this.apply() || attempt > 300) {
      if (attempt > 300) debug("gave up waiting for the widget's shadow tree");
      return;
    }
    requestAnimationFrame(() => this.schedule(attempt + 1));
  }

  /* One observer per shadow root, added as each is discovered. Re-applying is
     idempotent, so a noisy root costs nothing. */
  watch(root) {
    if (!root || this.observers.has(root)) return;
    const observer = new MutationObserver(() => this.apply());
    observer.observe(root, { childList: true, subtree: true });
    this.observers.set(root, observer);
  }

  /* Adopt a sheet into a shadow root once. Keyed on the root, so a re-rendered variant
     gets its own and a surviving one is not given a duplicate. */
  adopt(root, css) {
    if (!root || this.sheets.get(root) === css) return false;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    root.adoptedStyleSheets = [
      ...root.adoptedStyleSheets.filter((existing) => existing !== this.sheets.get(root)),
      sheet,
    ];
    this.sheets.set(root, css);
    this.applied ||= new WeakSet();
    this.applied.add(sheet);
    return true;
  }

  /* Returns true once every root this needs has been served, which is what tells
     schedule() to stop retrying. */
  apply() {
    const widgetRoot = this.widget?.shadowRoot;
    if (!widgetRoot) return false;
    this.watch(widgetRoot);

    /* The variant element depends on the app's Widget Style setting: opt-out, opt-in,
       checkbox, toggle or add-button. Match on shape rather than on one name so a
       change of that setting does not silently stop this working. */
    const variant = [...widgetRoot.children].find(
      (el) => el.tagName.startsWith("NARVAR-") && el.shadowRoot
    );
    if (!variant) return false;

    const variantRoot = variant.shadowRoot;
    this.watch(variantRoot);

    const card = variantRoot.querySelector("widget-card");
    const header = variantRoot.querySelector("widget-header");
    const title = variantRoot.querySelector("widget-title");

    const targets = [
      [variantRoot, SHEETS.variant],
      [card?.shadowRoot, SHEETS.card],
      [header?.shadowRoot, SHEETS.header],
      [title?.shadowRoot, SHEETS.title],
    ];
    const changed = targets.reduce(
      (acc, [root, css]) => this.adopt(root, css) || acc,
      false
    );
    const complete = targets.every(([root]) => root);

    if (changed) {
      debug("styles adopted", { variant: variant.tagName.toLowerCase(), complete });
      if (DEBUG) requestAnimationFrame(() => this.verify(variant));
    }
    return complete;
  }

  /* Confirm the result rather than assume it. Every check is a measured fact about the
     rendered box, so if Narvar renames a class the console says which step stopped
     working instead of the strip quietly reverting to their layout.

     Measure geometry, not declarations: `getComputedStyle(el).marginLeft` on an `auto`
     margin reports the used value in px, never the string "auto", which made an earlier
     version of this check cry wolf on a layout that was in fact correct. */
  verify(variant) {
    const root = variant.shadowRoot;
    const description = root.querySelector("widget-description");
    const card = root.querySelector("widget-card")?.shadowRoot?.querySelector(".card");
    const info = root.querySelector("widget-title")?.shadowRoot
      ?.querySelector("narvar-info-icon");
    const logo = root.querySelector("widget-title")?.shadowRoot?.querySelector("img.icon");

    /* The icon is where the design wants it when its right edge meets the card's inner
       right edge, which is the one thing `margin-left: auto` in a flex row guarantees. */
    let iconAtRight = "no .card or no narvar-info-icon";
    if (card && info) {
      const inner = card.getBoundingClientRect().right
        - (parseFloat(getComputedStyle(card).paddingRight) || 0);
      const gap = Math.abs(inner - info.getBoundingClientRect().right);
      iconAtRight = gap <= 2 || `off by ${Math.round(gap)}px`;
    }

    const checks = {
      blurbHidden: description ? getComputedStyle(description).display === "none" : "no widget-description",
      logoHidden: logo ? getComputedStyle(logo).display === "none" : "no img.icon",
      cardIsRow: card ? getComputedStyle(card).display === "flex" : "no .card",
      iconAtRight,
      iconStillClickable: info ? getComputedStyle(info).pointerEvents !== "none" : "no narvar-info-icon",
    };
    const broken = Object.entries(checks).filter(([, value]) => value !== true);

    if (broken.length) {
      console.warn(
        "[ia-narvar-modern] the widget's internals moved, the design is not applied:",
        Object.fromEntries(broken)
      );
    } else {
      debug("verified", { ...checks, cardHeight: Math.round(card.getBoundingClientRect().height) });
    }
  }
}

customElements.define("ia-narvar-modern", IaNarvarModern);
